import { useCallback } from 'react';
import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { createAsyncThunk } from "@reduxjs/toolkit";
import { useNavigate } from 'react-router-dom';

// Actions/selectors you already have:
import { allocatePortfolios } from '../../../modules/Inference/inferenceApi';
import { transformFormFields } from '../PathSummary/pathSummaryTransforms';
import { selectCurrentPath, selectStepperState, setMergedPortfolios } from '../../../store/slices/path.slice';
import { fetchDetailedPortfolioObjects } from "../../../api/portfolioApi";
import { selectFullPortfolios } from "../../../store/slices/portfolios.slice";
import {usePaths} from '../usePaths';
// This is our new "uber" thunk:
export const allocateAndMerge = createAsyncThunk(
  "path/allocateAndMerge",
  async ({ portfolioIds, formData }, { dispatch, getState, rejectWithValue }) => {
    try {
      // 1) Allocate
      const allocateResp = await dispatch(
        allocatePortfolios({
          portfolioIds,
          responses: formData,
        })
      ).unwrap();

      // The allocated data might be located under "allocateResp.data"
      // (depending on how your allocatePortfolios thunk is shaped)
      const allocatedData = allocateResp?.data ?? [];

      // 2) Determine which IDs need fetching
      const allocatedIds = allocatedData.map((p) => p.id).filter(Boolean);

      // 3) Fetch any missing portfolios
      await dispatch(fetchDetailedPortfolioObjects(allocatedIds));

      // 4) Merge allocated with full portfolio details from the store
      const state = getState();
      const fullPortfolios = selectFullPortfolios(state, allocatedIds);

      const merged = allocatedData
        .map((allocated) => {
          const full = fullPortfolios.find((p) => p.id === allocated.id);
          if (!full) return null;
          return {
            ...full,
            allocation: allocated.allocation,
            weight: allocated.wgt,
            reasoning: allocated.reasoning,
          };
        })
        .filter(Boolean);

      // 5) Set the merged portfolios in Redux
      dispatch(setMergedPortfolios(merged));

      // Return anything you want
      return merged;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);



function useConfirmPathSubmission() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Pull any Redux data needed to form the request payload.
  // You can also accept parameters if you prefer to pass them in
  // rather than always reading from the store:
  const { portfolioIds } = useSelector(selectCurrentPath);
  const { formData } = useSelector(selectStepperState);

  const confirmSubmission = useCallback(() => {
    Modal.confirm({
      title: 'Submit Path',
      content: 'Are you sure you want to submit this Path?',
      okText: 'Yes, Submit',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          const transformedData = transformFormFields(formData);
          await dispatch(
            allocateAndMerge({
              portfolioIds,
              formData: transformedData,
            })
          ).unwrap();
          navigate("/portfolios/paths/results");


          // Navigate to results page upon success
        } catch (error) {
          console.error('Error in submission process:', error);
          // Optionally show an error message
        }
      },
    });
}, [dispatch, navigate, portfolioIds, formData]);

  return { confirmSubmission };
}

export default useConfirmPathSubmission;
